/**
 * SessionBottomBar - Reusable bottom action bar for dictation/conversation pages.
 * Contains language selector, audio upload, and audio playback controls in a single row.
 */

import { LanguageSelector } from '@/components/LanguageSelector';
import { AudioProgressBar } from '@/components/session/AudioProgressBar';
import { Button } from '@/components/ui/button';
import type { Language } from '@/types/session';
import { Download, Play, Square } from 'lucide-react';
import { type ReactNode, useImperativeHandle, useRef, useState } from 'react';

interface SessionBottomBarProps {
  /** Language selection */
  languages: Language[];
  transcriptionLanguage: string;
  summaryLanguage: string;
  onTranscriptionLanguageChange: (lang: string) => void;
  onSummaryLanguageChange: (lang: string) => void;
  languageDisabled?: boolean;

  /** Audio playback */
  recordedAudioUrl: string | null;
  onAudioDurationLoaded?: (durationSeconds: number) => void;
  downloadFilename?: string;

  /** Slot for page-specific audio upload component */
  audioUpload?: ReactNode;

  /** Ref for external audio control (e.g. seek-to-time) */
  audioRef?: React.Ref<SessionBottomBarAudioHandle>;
}

export interface SessionBottomBarAudioHandle {
  seekTo: (time: number) => void;
}

export const SessionBottomBar = ({
  languages,
  transcriptionLanguage,
  summaryLanguage,
  onTranscriptionLanguageChange,
  onSummaryLanguageChange,
  languageDisabled,
  recordedAudioUrl,
  onAudioDurationLoaded,
  downloadFilename = 'recording.wav',
  audioUpload,
  audioRef: externalAudioRef,
}: SessionBottomBarProps) => {
  const internalAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState('');
  const [audioCurrentTime, setAudioCurrentTime] = useState('0:00');
  const [audioProgress, setAudioProgress] = useState(0);

  useImperativeHandle(externalAudioRef, () => ({
    seekTo: (time: number) => {
      const audio = internalAudioRef.current;
      if (!audio) return;

      audio.currentTime = time;
      if (audio.paused) {
        const onSeeked = () => {
          audio.removeEventListener('seeked', onSeeked);
          audio.play();
          setIsAudioPlaying(true);
        };
        audio.addEventListener('seeked', onSeeked);
      }
    },
  }));

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const hasAudio = !!recordedAudioUrl;

  return (
    <div className="hidden flex-col border-border border-t bg-white text-muted-foreground text-xs md:flex">
      {/* Single row: language | upload | playback controls */}
      <div className="flex items-center gap-2 px-4 py-2">
        <LanguageSelector
          languages={languages}
          transcriptionLanguage={transcriptionLanguage}
          summaryLanguage={summaryLanguage}
          onTranscriptionChange={onTranscriptionLanguageChange}
          onSummaryChange={onSummaryLanguageChange}
          disabled={languageDisabled}
        />

        {audioUpload}

        <div className="mx-1 h-4 w-px bg-border" />

        {recordedAudioUrl && (
          /* biome-ignore lint/a11y/useMediaCaption: Dynamically recorded audio doesn't have pre-made captions */
          <audio
            ref={internalAudioRef}
            src={recordedAudioUrl}
            preload="auto"
            onLoadedMetadata={(e) => {
              const duration = e.currentTarget.duration;
              setAudioDuration(formatDuration(duration));
              onAudioDurationLoaded?.(duration);
            }}
            onTimeUpdate={(e) => {
              const current = e.currentTarget.currentTime;
              const duration = e.currentTarget.duration;
              setAudioCurrentTime(formatDuration(current));
              setAudioProgress(duration > 0 ? (current / duration) * 100 : 0);
            }}
            onPlay={() => setIsAudioPlaying(true)}
            onPause={() => setIsAudioPlaying(false)}
            onEnded={() => {
              setIsAudioPlaying(false);
              setAudioCurrentTime('0:00');
              setAudioProgress(0);
            }}
            className="hidden"
          />
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-9 w-9 shrink-0 p-0 hover:bg-muted hover:text-primary"
          disabled={!hasAudio}
          onClick={() => {
            if (internalAudioRef.current) {
              if (isAudioPlaying) {
                internalAudioRef.current.pause();
              } else {
                internalAudioRef.current.play();
              }
            }
          }}
          title={isAudioPlaying ? 'Stöðva' : 'Spila upptöku'}
        >
          {isAudioPlaying ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
        <span className="min-w-[36px] shrink-0 tabular-nums">{hasAudio ? audioCurrentTime : '0:00'}</span>
        <AudioProgressBar
          progress={audioProgress}
          disabled={!hasAudio}
          onSeek={(percent) => {
            setAudioProgress(percent);
            if (internalAudioRef.current) {
              internalAudioRef.current.currentTime = (percent / 100) * internalAudioRef.current.duration;
            }
          }}
        />
        <span className="min-w-[36px] shrink-0 tabular-nums">{hasAudio ? audioDuration || '0:00' : '0:00'}</span>
        <Button
          variant="ghost"
          size="sm"
          className="h-9 w-9 shrink-0 p-0 hover:bg-muted hover:text-primary"
          disabled={!hasAudio}
          onClick={() => {
            if (recordedAudioUrl) {
              const a = document.createElement('a');
              a.href = recordedAudioUrl;
              a.download = downloadFilename;
              a.click();
            }
          }}
          title="Niðurhala"
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
